import React, { useCallback, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Box, Button, Flex, FormField, Input, Label, Tooltip } from 'pcln-design-system'

import { Warning } from 'pcln-icons'

const MaxWidthFormField = styled(FormField)`
  max-width: 155px;
  & label {
    font-size: 10px;
  }
`

const propTypes = {
  absoluteMax: PropTypes.number,
  absoluteMin: PropTypes.number,
  className: PropTypes.string,
  labelMax: PropTypes.string,
  labelMin: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
}

const defaultProps = {
  onChange: () => {},
}

const isValidNumber = (newValue, validMin, validMax) => {
  if (newValue !== undefined && newValue?.replace?.(/[0-9]/g, '') === '') {
    const numValue = parseInt(newValue, 10)
    return numValue >= validMin && numValue <= validMax
  }
  return false
}

const getValidationMessage = (fieldLabel, newValue, validMin, validMax) => {
  const startText = fieldLabel ? fieldLabel + ' must' : 'Must'
  if (newValue !== undefined && newValue?.replace?.(/[0-9]/g, '') === '') {
    return `${startText} be between ${validMin} and ${validMax}.`
  }
  return `${startText} be numeric.`
}

let validationTimer

const RangeSliderNumberInput = ({ absoluteMin, absoluteMax, labelMax, labelMin, max, min, onChange }) => {
  const [disableApply, setDisableApply] = useState(true)
  const [isValid, setValid] = useState({ min: true, max: true })
  const [maxHasFocus, setMaxFocus] = useState(false)
  const [maxValue, setMax] = useState(`${max}`)
  const [minValue, setMin] = useState(`${min}`)

  const validateNumbers = useCallback(() => {
    const minValid = isValidNumber(minValue, absoluteMin, absoluteMax)
    let maxValid = isValidNumber(maxValue, absoluteMin, absoluteMax)
    const intMinValue = parseInt(minValue, 10)
    const intMaxValue = parseInt(maxValue, 10)
    if (minValid && maxValid) {
      maxValid = intMinValue <= intMaxValue
    }

    const minValueMax = maxValid ? intMaxValue : absoluteMax
    const maxValueMin = minValid ? intMinValue : absoluteMin

    const minMsg = !minValid ? getValidationMessage(labelMin, minValue, absoluteMin, minValueMax) : ''
    const maxMsg = !maxValid ? getValidationMessage(labelMax, maxValue, maxValueMin, absoluteMax) : ''

    const newValid = {
      min: minValid,
      minMsg,
      max: maxValid,
      maxMsg,
    }
    setValid(newValid)
    setDisableApply(!maxValid || !minValid || (min === intMinValue && max === intMaxValue))
  }, [absoluteMax, absoluteMin, labelMax, labelMin, max, maxValue, min, minValue])

  useEffect(() => {
    setMax(`${max}`)
  }, [max])

  useEffect(() => {
    setMin(`${min}`)
  }, [min])

  useEffect(() => {
    setDisableApply(true)
    clearTimeout(validationTimer)
    validationTimer = setTimeout(validateNumbers, 750)
  }, [validateNumbers])

  const handleApply = () => {
    onChange([parseInt(minValue, 10), parseInt(maxValue, 10)])
  }

  const handleKeyPress = (e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault()
    }
  }

  const formattedMax =
    !isValid.max || parseInt(maxValue, 10) < absoluteMax || maxHasFocus ? maxValue : `${maxValue}+`

  return (
    <>
      <Flex justifyContent='space-between' mt={3}>
        <Box mr={3}>
          <MaxWidthFormField isValid={isValid.min}>
            <Label>{labelMin}</Label>
            <Input
              name='slider_input_min'
              onChange={(e) => {
                setMin(e?.target?.value)
              }}
              onKeyPress={handleKeyPress}
              value={minValue}
            ></Input>
            {!isValid.min && <Warning color='error' />}
          </MaxWidthFormField>
          {!isValid.min && (
            <Tooltip bottom right color='error'>
              {isValid.minMsg}
            </Tooltip>
          )}
        </Box>
        <Box>
          <MaxWidthFormField isValid={isValid.max}>
            <Label>{labelMax}</Label>
            <Input
              name='slider_input_max'
              onBlur={() => {
                maxHasFocus && setMaxFocus(false)
              }}
              onChange={(e) => {
                setMax(e?.target?.value)
              }}
              onFocus={() => {
                !maxHasFocus && setMaxFocus(true)
              }}
              onKeyPress={handleKeyPress}
              value={formattedMax}
            ></Input>
            {!isValid.max && <Warning color='error' />}
          </MaxWidthFormField>
          {!isValid.max && (
            <Tooltip bottom right color='error'>
              {isValid.maxMsg}
            </Tooltip>
          )}
        </Box>
      </Flex>
      <Flex justifyContent='flex-end'>
        <Button mt={2} onClick={handleApply} size='small' disabled={disableApply}>
          Apply
        </Button>
      </Flex>
    </>
  )
}

RangeSliderNumberInput.propTypes = propTypes
RangeSliderNumberInput.defaultProps = defaultProps
export default RangeSliderNumberInput
